// src/pages/DdosTestingPage.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ServerInfoCard from '../components/Shared/ServerInfoCard';
import DdosTestingHeader from '../components/DdosTesting/DdosTestingHeader';
import AttackTypeSelector from '../components/DdosTesting/AttackTypeSelector';
import AttackConfiguration from '../components/DdosTesting/AttackConfiguration';
import AttackMonitoring from '../components/DdosTesting/AttackMonitoring';
import TestResults from '../components/DdosTesting/TestResults';
import ProtectionAnalysis from '../components/DdosTesting/ProtectionAnalysis';
import Tips from '../components/Shared/Tips';
import PageTransition from '../components/Shared/PageTransition';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
// Import CORRECT connection config
// Keep DDoS config for attack-related messages
import { WS_DDOS_TYPES, WS_DDOS_TOPICS, getAppEndpoint, getUserQueueTopic } from '../config/wsDdosConfig';
import { useServerConnection } from '../contexts/ServerConnectionContext';
import ProcessStatusDisplay from '../components/Shared/ProcessStatusDisplay';
import { useNotifications } from '../contexts/NotificationContext';
import { handleWebSocketError } from '../utils/websocketErrorHandler';

const tips = [
  {
    icon: 'fa-bolt',
    title: 'Attack Duration',
    description: 'Short tests (30-60 seconds) are usually sufficient to assess vulnerabilities without causing extended downtime. Longer tests increase detection risk and may trigger automatic mitigation systems.'
  },
  {
    icon: 'fa-shield-alt',
    title: 'Use VPN',
    description: 'Always use a VPN when performing DDoS testing to protect your identity and avoid potential IP bans. Rotate VPN endpoints between tests to avoid blacklisting.'
  },
  {
    icon: 'fa-exclamation-triangle',
    title: 'Legal Considerations',
    description: 'DDoS testing without explicit permission is illegal in most jurisdictions and could result in serious legal consequences. Always obtain written permission from server owners before conducting tests.'
  }
];

const DdosTestingPage = ({openModal}) => {
  const { isAuthenticated, user } = useAuth();
  const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket();
  const {addNotification} = useNotifications();

  // --- State ---
  const [attackConfig, setAttackConfig] = useState({
    type: 'packet',
    method: 'syn-flood',
    intensity: 65,
    threads: 16,
    duration: { value: 2, unit: 'minutes' }
  });
  const [attackState, setAttackState] = useState({
    status: 'idle', // 'idle', 'starting', 'running', 'stopping', 'completed', 'error'
    message: null,
    isLoading: false, // Loading state specifically for starting/stopping attack
  });
  const [attackProgress, setAttackProgress] = useState(null);
  const [attackFinalResults, setAttackFinalResults] = useState(null);
  const [protectionInfo, setProtectionInfo] = useState(null);
  const [testHistory, setTestHistory] = useState([]);
  const [ddosError, setDdosError] = useState(null); // Separate error state for DDoS operations
  const { serverInfo, isConnectedToServer, connectionError, isConnecting } = useServerConnection();
  const [ddosConsoleLines, setDdosConsoleLines] = useState([]);

  // Store unsubscribe functions
  const subscriptions = useRef({
    serverInfo: null, // For /user/queue/server/info
    serverError: null, // For /user/queue/server/error
    ddosStatus: null, // For /user/queue/ddos/status
    ddosProgress: null, // For /user/queue/ddos/progress
    ddosError: null, // For /user/queue/ddos/error
  });

  // --- Helper to reset state ---
  const resetAttackState = (keepServerConnection = false) => {
    console.log("Resetting DDoS Attack State, Keep Connection:", keepServerConnection);
    setAttackState({ status: 'idle', message: null, isLoading: false });
    setAttackProgress(null);
    setAttackFinalResults(null);
    setDdosError(null);
    // Optionally reset config to defaults
    // setAttackConfig({ type: 'packet', ... });
    if (!keepServerConnection) {
      setProtectionInfo(null);
    } else if (serverInfo) {
      // If keeping connection, ensure status reflects readiness
      setAttackState({ status: 'idle', message: 'Connected to target.', isLoading: false });
    }
  };

  // Helper to add DDoS specific console lines
  const addDdosConsoleLine = useCallback((text, type = 'info') => {
    setDdosConsoleLines(prev => {
      const newLine = { type, text, timestamp: new Date().toISOString() };
      const limitedLines = [...prev, newLine].slice(-200);
      return limitedLines;
    });
  }, []);


  // --- WebSocket Message Handlers ---

  // Handles messages related to the INITIAL server connection

  // Handles messages specifically for DDoS Status (/user/queue/ddos/status)
  const handleDdosStatusUpdate = useCallback((message) => {
    console.log('%cWS DDoS STATUS Received:', 'color: blue;', message);
    setDdosError(null); // Clear DDoS-specific error on new status

    switch (message?.type) {
      // DDOS_CONNECTION_STATUS is no longer expected here for initial connect
      // It might be used if DDoS backend does its own connection check, but primary is ServerInfo

      case WS_DDOS_TYPES.ATTACK_STARTED:
        setAttackState({ status: 'running', message: message.message || `Attack (${message.attackType}) started.`, isLoading: false });
        console.log("Status: ", attackState.status);
        setAttackProgress(null);
        setAttackFinalResults(null);
        console.log("Attack started:", message);
        setDdosConsoleLines([]); // Clear console for new attack
        addDdosConsoleLine(`Attack Started: ${message.message || message.attackType}`, 'system');
        break;

      case WS_DDOS_TYPES.ATTACK_STOPPED:
        setAttackState({ status: 'idle', message: message.message || 'Attack stopped.', isLoading: false });
        setAttackProgress(null);
        // Optionally clear progress: setAttackProgress(null);
        console.log("Attack stopped:", message);
        addDdosConsoleLine(`Attack Stopped: ${message.message || 'User initiated stop.'}`, message.success ? 'warning' : 'error');
        break;

      case WS_DDOS_TYPES.ATTACK_COMPLETED:
        setAttackState({ status: 'completed', message: message.message || 'Attack finished.', isLoading: false });
        setAttackFinalResults(message.results || {});
        setAttackProgress(null);
        setProtectionInfo(prev => message.protectionAnalysis || prev); // Update protection info if sent on completion
        console.log("Attack completed:", message);

        const success = message.results?.success ?? false;
        addDdosConsoleLine(`Attack Completed: ${message.message || (success ? 'Success' : 'Failed')}`, success ? 'success' : 'error');
        // Optionally log key results
        if (message.results) {
          addDdosConsoleLine(`Final Target Status: ${message.results.finalTargetStatus || 'N/A'}`, 'info');
          addDdosConsoleLine(`Max Packets/s: ${message.results.maxPacketsPerSec || 'N/A'}`, 'info');
        }
        break;

      case WS_DDOS_TYPES.CURRENT_STATE:
        // Handle DDoS state recovery after reconnect IF already connected
        if (isConnectedToServer && message.attack) {
          // Restore ongoing attack state
          setAttackConfig(message.attack.config || attackConfig);
          setAttackState({ status: message.attack.status || 'running', message: 'Reconnected to ongoing attack.', isLoading: false });
          setAttackProgress(message.attack.progress || null);
          console.log("Restored DDoS attack state from server:", message);
        } else if (isConnectedToServer) {
          setAttackState({ status: 'idle', message: 'Reconnected to target (no active attack).', isLoading: false });
        } else {
          // If we get state but aren't connected via server info, something is wrong
          console.warn("Received DDoS state but not connected via ServerInfo. Ignoring.");
        }
        break;

      default:
        console.warn('Unhandled DDoS status message type:', message?.type);
    }
  }, [isConnectedToServer, attackConfig, addDdosConsoleLine]); // Removed protectionInfo, added isConnectedToServer

  // Handles messages for DDoS Progress (/user/queue/ddos/progress)
  const handleDdosProgressUpdate = useCallback((message) => {
    // console.log('%cWS DDoS PROGRESS Received:', 'color: green;', message); // Can be noisy for frequent updates
    if (message?.type === WS_DDOS_TYPES.ATTACK_PROGRESS && message.data) {
        // Always set the progress data if the type is correct.
        // The AttackMonitoring component will use its `attackStatus` prop (derived from attackState.status)
        // to decide whether to show "Active" visuals, but will always use the latest progress data for stats.
        setAttackProgress(message.data); // Store only the .data part of the message

        // Log to DDoS console occasionally (as it was before)
        if (Math.random() < 0.2) { // Increased chance slightly for debugging if needed
            addDdosConsoleLine(`Live Update: ${message.data.packetsSent || 0} packets, Ping: ${message.data.serverPing || 'N/A'}ms, Status: ${message.data.targetStatus || 'N/A'}`, 'info');
        }
    } else {
        console.warn('[DdosTestingPage] Unhandled/unexpected progress message type or missing data in progress update:', message);
    }
  }, [addDdosConsoleLine]); // `addDdosConsoleLine` is memoized and stable.
                           // `attackState.status` is removed from deps; its check was problematic here.

  // Handles messages for DDoS Errors (/user/queue/ddos/error)
  // Handles messages for DDoS Errors (/user/queue/ddos/error)
  const handleDdosErrorUpdate = useCallback((errorDataFromServer) => {
    console.log('[DdosTestingPage] handleDdosErrorUpdate triggered. Raw errorDataFromServer:', errorDataFromServer);

    if (!errorDataFromServer || typeof errorDataFromServer !== 'object') {
      console.error('[DdosTestingPage] Invalid errorDataFromServer received:', errorDataFromServer);
      const genericMsg = "Received an invalid error format from server.";
      setDdosError(genericMsg);
      setAttackState(prev => ({
        status: 'error',
        message: genericMsg,
        isLoading: false,
      }));
      handleWebSocketError(genericMsg, null, openModal, addNotification, {
        defaultTitle: 'DDoS System Error',
        moduleName: 'DDoS Testing',
      });
      return;
    }

    const actualErrorMessage = errorDataFromServer.error || "An unspecified DDoS error occurred.";
    console.log('[DdosTestingPage] Extracted actualErrorMessage:', actualErrorMessage);

    handleWebSocketError(
      errorDataFromServer,
      null,
      openModal,
      addNotification,
      {
        defaultTitle: 'DDoS Operation Error',
        moduleName: 'DDoS Testing',
      }
    );

    setDdosError(actualErrorMessage);
    setAttackState(prev => {
      console.log('[DdosTestingPage] Updating attack state. Prev:', prev, 'New status: error, isLoading: false, message:', actualErrorMessage);
      return {
        status: 'error',
        message: actualErrorMessage,
        isLoading: false,
      };
    });
    setAttackProgress(null);
    console.log('[DdosTestingPage] State update calls completed in handleDdosErrorUpdate.');

  }, [openModal, addNotification]); // setDdosError is stable, removed from deps

  // --- WebSocket Subscriptions and State Recovery ---
  useEffect(() => {
    if (!isWebSocketConnected || !isAuthenticated) {
      // Ensure cleanup if WebSocket disconnects or user logs out
      Object.values(subscriptions.current).forEach(unsub => unsub?.());
      subscriptions.current = { serverInfo: null, serverError: null, ddosStatus: null, ddosProgress: null, ddosError: null };
      return;
    }

    console.log("DdosTestingPage: Main subscription useEffect triggered. Subscribing to DDoS topics.");

    // Unsubscribe from previous before subscribing again
    Object.values(subscriptions.current).forEach(unsub => unsub?.());

    // Subscribe to DDoS specific topics
    // The handlers (e.g., handleDdosStatusUpdate) are memoized by useCallback.
    // The `subscribe` function from `useWebSocket` should internally ensure it calls the
    // latest version of these handlers if their identities change due to their own dependencies.
    subscriptions.current.ddosStatus = subscribe(getUserQueueTopic(WS_DDOS_TOPICS.STATUS), handleDdosStatusUpdate);
    subscriptions.current.ddosProgress = subscribe(getUserQueueTopic(WS_DDOS_TOPICS.PROGRESS), handleDdosProgressUpdate);
    subscriptions.current.ddosError = subscribe(getUserQueueTopic(WS_DDOS_TOPICS.ERROR), handleDdosErrorUpdate);

    // Request DDoS state IF connection is already established
    // This part is important for re-connection scenarios.
    // Check if serverInfo exists (meaning initial connection handshake was successful)
    // and no attack is currently marked as active in UI, to avoid redundant GET_STATE if already handling one.
    if (serverInfo && attackState.status === 'idle') {
        console.log("DdosTestingPage: Requesting GET_STATE as serverInfo exists and attack is idle.");
        sendMessage(getAppEndpoint(WS_DDOS_TOPICS.GET_STATE), {});
    }


    // Cleanup function
    return () => {
      console.log("DdosTestingPage: Main subscription useEffect cleanup. Unsubscribing from DDoS topics.");
      Object.values(subscriptions.current).forEach(unsub => unsub?.());
      subscriptions.current = { serverInfo: null, serverError: null, ddosStatus: null, ddosProgress: null, ddosError: null };
    };
  }, [
    // Critical dependencies that define the subscription lifecycle:
    isWebSocketConnected,
    isAuthenticated,
    subscribe, // The subscribe function from useWebSocket
    sendMessage, // If used for GET_STATE within this effect
    // serverInfo is added here specifically because GET_STATE logic depends on it.
    // If GET_STATE was handled elsewhere, serverInfo might not be needed here.
    serverInfo,
    // The handlers themselves are NOT direct dependencies for *re-subscribing*
    // if `useWebSocket` hook's `subscribe` correctly handles updated callbacks.
    // The goal is to prevent STOMP-level unsubscription/re-subscription when handlers update.
  ]);


  // --- Component Actions ---
  const handleConfigChange = useCallback((newConfig) => {
    setAttackConfig(prev => ({ ...prev, ...newConfig }));
  }, []);

  const handleStartAttack = useCallback(() => {
    setDdosConsoleLines([]); // Clear console on new attack start
    addDdosConsoleLine(`Initiating ${attackConfig.type} attack (${attackConfig.method}) on ${serverInfo.ip}...`, 'system');

    if (!isConnectedToServer || !serverInfo?.ip || attackState.status === 'running' || attackState.isLoading) {
      console.warn("Cannot start attack. Conditions not met.", { isConnectedToServer, serverIp: serverInfo?.ip, attackStatus: attackState.status, attackLoading: attackState.isLoading });
      setDdosError("Connect to a server first or wait for the current action to complete.");
      return;
    }
    console.log(`Starting attack on ${serverInfo.ip} with config:`, attackConfig);
    setAttackState({ status: 'starting', message: 'Initiating attack...', isLoading: true });
    setDdosError(null);
    setAttackProgress(null);
    setAttackFinalResults(null);

    // Send START message to the DDoS specific endpoint
    sendMessage(getAppEndpoint(WS_DDOS_TOPICS.START), {
      ip: serverInfo.ip,
      attackConfig: attackConfig
    });
  }, [isConnectedToServer, serverInfo, attackState, attackConfig, sendMessage, addDdosConsoleLine]);

  const handleStopAttack = useCallback(() => {
    if (!serverInfo?.ip || (attackState.status !== 'running' && attackState.status !== 'starting') || attackState.isLoading) {
      console.warn("Cannot stop attack. Conditions not met or no attack running/starting.");
      setDdosError("No active attack to stop."); // Set DDoS error
      addDdosConsoleLine('Requesting attack stop...', 'system');
      return;
    }
    console.log(`Stopping attack on ${serverInfo.ip}`);
    setAttackState({ status: 'stopping', message: 'Stopping attack...', isLoading: true });
    setDdosError(null);

    // Send STOP message to the DDoS specific endpoint
    sendMessage(getAppEndpoint(WS_DDOS_TOPICS.STOP), { ip: serverInfo.ip });
  }, [serverInfo, attackState, sendMessage, addDdosConsoleLine]);

  // --- Derived State ---
  const isAttackActive = attackState.status === 'running' || attackState.status === 'starting';
  // Combined error display logic
  const displayError = ddosError || connectionError;
  // Disable interactions if connecting, starting/stopping attack, or not connected to server (after initial attempt)
  const isInteractionDisabled = isConnecting || attackState.isLoading;
  // Disable specific DDoS tools if not connected successfully
  const toolsDisabled = !isConnectedToServer || isConnecting || attackState.isLoading;


  return (
    <PageTransition>
      <div className="p-4 sm:p-6 lg:p-8">

        <DdosTestingHeader />
        <ServerInfoCard />

        {/* Display DDoS-specific errors separately */}
        {ddosError && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-red-900/30 border border-accent-red/50 rounded-lg text-accent-red text-sm animate-fade-in"
          >
            <i className="fas fa-exclamation-triangle mr-2"></i>
            <strong>DDoS Error:</strong> {ddosError}
          </motion.div>
        )}


        <AnimatePresence>
          {/* Only show DDoS tools if connection attempt finished AND server is online */}
          {(
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <AttackTypeSelector
                selectedType={attackConfig.type}
                onTypeChange={(type) => handleConfigChange({ type })}
                isDisabled={isAttackActive || attackState.isLoading || !isConnectedToServer} // Disable during attack activity
              />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <AttackConfiguration
                  attackConfig={attackConfig}
                  onConfigChange={handleConfigChange}
                  onStartAttack={handleStartAttack}
                  onStopAttack={handleStopAttack}
                  isAttackRunning={isAttackActive}
                  isLoading={attackState.isLoading}
                  isDisabled={!isConnectedToServer || isConnecting} // Base disable on server connection status
                />
                {/* Use AttackMonitoring for the main visual feedback */}
                <AttackMonitoring
                  className="lg:col-span-2"
                  attackProgress={attackProgress} // Pass the actual data part of the progress message
                  attackStatus={attackState.status}
                  serverInfo={serverInfo}
                />
              </div>

              {/* Test Results and Protection Analysis remain below the main grid */}
              {attackFinalResults && (
                <TestResults results={attackFinalResults} />
              )}
              {protectionInfo && (
                <ProtectionAnalysis
                  protections={protectionInfo.detected || []}
                  recommendations={protectionInfo.recommendations || []}
                />
              )}

              {/* Dedicated section for DDoS Attack Log below the main grid */}
              {isConnectedToServer && ( // Show log only if connected
                <div className="mt-8"> {/* Added margin top for spacing */}
                  <ProcessStatusDisplay
                    lines={ddosConsoleLines}
                    status={attackState.status === 'idle' && serverInfo ? 'Ready' : attackState.status}
                    statusColor={
                      attackState.status === 'running' ? 'text-accent-green' :
                      attackState.status === 'starting' || attackState.status === 'stopping' ? 'text-accent-orange' :
                      attackState.status === 'error' ? 'text-accent-red' :
                      attackState.status === 'completed' ? 'text-accent-green' : // color for completed
                                  'text-gray-400' // idle
                    }
                    isLoading={attackState.isLoading || attackState.status === 'starting' || attackState.status === 'stopping'}
                    error={null} // Main DDoS error is displayed separately above
                    showProgressBar={attackState.status === 'running' && !!attackProgress?.data} // Show a simple progress indication if running
                    progressData={attackProgress?.data ? { // Use .data for progress
                        scanned: attackProgress.data.packetsSent || 0,
                        total: 0, // DDoS is ongoing, no fixed total for this simple log progress
                        found: 0 // Not applicable here
                    } : { scanned: 0, total: 0, found: 0 }}
                    actions={[
                      {
                        label: "Clear Log",
                        onClick: () => setDdosConsoleLines([]),
                        icon: "fa-trash",
                        disabled: attackState.isLoading || ddosConsoleLines.length === 0,
                        primary: false
                      }
                    ]}
                    isConsoleInputEnabled={false} // No input for DDoS console log
                    consoleTitle="DDoS Attack Log"
                    consoleMaxHeight="h-[280px]" // Adjusted height for a more compact log
                    showHeader={true} // Show the title and status for the log
                    showMainBorder={true} // Keep the border for the log section
                    className="bg-dark-700" // Slightly different background for distinction if desired
                  />
                </div>
              )}
              {attackFinalResults && (
                <TestResults results={attackFinalResults} />
              )}
              {protectionInfo && (
                <ProtectionAnalysis
                  protections={protectionInfo.detected || []}
                  recommendations={protectionInfo.recommendations || []}
                />
              )}
            </motion.div>
          )}
          {/* Optional: Show a message if connection failed or server offline */}
          {serverInfo && !isConnecting && !isConnectedToServer && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="text-center text-gray-400 p-6 bg-dark-600 rounded-2xl mb-8 border border-dark-400"
            >
              <i className="fas fa-plug-circle-xmark text-3xl mb-3 text-accent-orange"></i>
              <p>{connectionError || "Cannot configure DDoS attack. Connect to an online server first."}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tips can always be visible */}
        <Tips title="Ddos Testing Tips" accentColor='yellow' tips={tips} />

      </div>
    </PageTransition>
  );
};

export default DdosTestingPage;